import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Button,
  Box,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import HighlightAltIcon from '@mui/icons-material/HighlightAlt';
import PublishIcon from '@mui/icons-material/Publish';
import EdgeOrREST from '../EdgeOrREST';
import { handleReportCaptureClick } from './reportCaptureUtils';
import { useSelector } from 'react-redux';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import Loading from '../components/shared/loading.component';

export default function ReportActions(props) {
  const {
    // Props for component functionality
    handleAddReportCaptureArea,
    //generateReport,
    onTemplateChange,
    setSnack,
    // Props to be bundled into reportOptions
    stageRef,
    reportCaptureAreas,
    setReportCaptureAreas,
    masks,
    setMasks,
    realWorldWidth,
    imagePixelWidth,
    realWorldHeight,
    imagePixelHeight,
    grids,
    areaGridResults,
    areaResults,
    columns,
    gridScaleX,
    gridScaleY,
    dxfContainerRef,
    dxfScale,
    dxfViewer,
    syncStageFromViewer,
    templates,
    setTemplates,
  } = props;

  // State for UI elements and data fetching
  const [withMasks, setWithMasks] = useState(true);
  const [loading, setLoading] = useState(true);
  const [projectName, setProjectName] = useState('Project 1');
  const [projectNumber, setProjectNumber] = useState('0001');
  const [fontSize, setFontSize] = useState(10);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [customLogo, setCustomLogo] = useState(null);
  const [htmlPreviewOpen, setHtmlPreviewOpen] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const user = useSelector(state => state.user);

  // --- START: Defined options for new dropdowns ---
  const fontSizes = [8, 9, 10, 11, 12];
  const fontFamilies = [
    'Arial',
    'Verdana',
    'Times New Roman',
    'Courier New',
    'Georgia',
    'Helvetica',
  ];
  // --- END: Defined options for new dropdowns ---

  // Fetch templates from the backend on component mount
  useEffect(() => {
    EdgeOrREST(null, 'GET_TEMPLATES', onGetTemplates, null);
  }, []);

  const onGetTemplates = (response) => {
    if (response && response.status === 200) {
      setTemplates(response.data);
    } else {
      if (setSnack) {
        setSnack({
          open: true,
          message: 'Could not load templates.',
          severity: 'error',
        });
      }
    }
    setLoading(false);
  };

  // Handler for template selection changes
  const handleTemplateSelectionChange = (event) => {
    const selectedId = event.target.value;
    const selectedTemplate = templates.find((template) => String(template.id) === String(selectedId)) || null;
    if (onTemplateChange) {
      onTemplateChange(selectedTemplate);
    }
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        // Store the Base64 string of the uploaded file in our state
        setCustomLogo(e.target.result);
        if (setSnack) {
          setSnack({ open: true, message: 'Custom icon loaded.', severity: 'success' });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Handler for the "Generate Report" button click
  const handleGenerateReport = (viewTemplate) => {
    if (!props.selectedTemplate) {
      if (setSnack) {
        setSnack({
          open: true,
          message: 'Please select a template first.',
          severity: 'warning',
        });
      }
      return;
    }

    console.log("Generating report with template:", props.selectedTemplate);
    const templateElements = props.selectedTemplate.elements;
    let parsedTemplateElements = [];
    if (Array.isArray(templateElements)) {
      parsedTemplateElements = templateElements;
    } else if (typeof templateElements === 'string') {
      try {
        parsedTemplateElements = JSON.parse(templateElements || '[]');
      } catch (error) {
        console.warn('Failed to parse template elements:', error);
        parsedTemplateElements = [];
      }
    }

    if (!Array.isArray(parsedTemplateElements) || parsedTemplateElements.length === 0) {
      if (setSnack) {
        setSnack({
          open: true,
          message: 'Template is empty. Please add elements before generating a report.',
          severity: 'warning',
        });
      }
      return;
    }

    // Bundle all necessary data for the report generation function
    const reportOptions = {
      // Pass the whole template object now
      selectedTemplate: props.selectedTemplate,
      stageRef,
      reportCaptureAreas,
      setReportCaptureAreas,
      masks,
      setMasks,
      realWorldWidth,
      imagePixelWidth,
      realWorldHeight,
      imagePixelHeight,
      setSnack,
      grids,
      areaGridResults,
      areaResults,
      columns,
      gridScaleX,
      gridScaleY,
      dxfContainerRef,
      dxfScale,
      dxfViewer,
      syncStageFromViewer,
      fontSize,
      fontFamily,
      projectName,
      projectNumber,
      user,
      customLogo,
      viewTemplate: viewTemplate || false,
      setHtmlPreviewOpen,
      setHtmlContent,
      setIsLoading,
    };

    // Call the main generateReport function passed from the parent
    handleReportCaptureClick(withMasks, reportOptions);
  };

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        gap={1.5}
        flexWrap="wrap"
        sx={{ p: 1 }}
      >
        {/* --- Template Select --- */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="template-select-label">Template</InputLabel>
          <Select
            labelId="template-select-label"
            value={props.selectedTemplate?.id ?? ''}
            label="Template"
            onChange={handleTemplateSelectionChange}
            disabled={loading}
            renderValue={(selectedId) => {
              const selectedTemplate = templates.find((template) => String(template.id) === String(selectedId));

              return selectedTemplate ? (
                <Tooltip title={selectedTemplate.templateName}>
                  <Typography
                    noWrap
                    sx={{ maxWidth: '100%', display: 'block' }}
                  >
                    {selectedTemplate.templateName}
                  </Typography>
                </Tooltip>
              ) : (
                ''
              );
            }}
          >
            {loading ? (
              <MenuItem disabled>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Loading...
              </MenuItem>
            ) : (
              templates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.templateName}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        {/* --- Action Buttons (stacked) --- */}
        <Stack
          direction="column"
          spacing={1}
          sx={{ width: '100%' }}
        >
          <Tooltip title="Create Template">
            <Button
              onClick={props.createTemplate}
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              sx={{ color: 'green', borderColor: 'green', justifyContent: 'flex-start' }}
              fullWidth
            >
              Create Template
            </Button>
          </Tooltip>

          <Tooltip title="Edit Template">
            <Button
              onClick={props.toggleTemplateEditor}
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              sx={{ justifyContent: 'flex-start' }}
              fullWidth
            >
              Edit Template
            </Button>
          </Tooltip>

          <Tooltip title="View Template">
            <Button
              onClick={() => handleGenerateReport(true)}
              variant="outlined"
              size="small"
              startIcon={<SearchIcon />}
              sx={{ color: 'orange', borderColor: 'orange', justifyContent: 'flex-start' }}
              fullWidth
            >
              View Template
            </Button>
          </Tooltip>

          <Tooltip title="Delete Template">
            <Button
              onClick={props.deleteTemplate}
              variant="outlined"
              size="small"
              startIcon={<DeleteIcon />}
              sx={{ color: 'red', borderColor: 'red', justifyContent: 'flex-start' }}
              fullWidth
            >
              Delete Template
            </Button>
          </Tooltip>
        </Stack>

        <Typography variant="subtitle1" gutterBottom>
          Edit report information:
        </Typography>

        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: '#fafafa',
          }}
        >

          {/* --- ROW 1: Project Name + Number --- */}
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">

            <TextField
              label="Project Name"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Project 1"
              size="small"
              fullWidth
              InputProps={{
                sx: { height: 32 },
              }}
              inputProps={{ maxLength: 50 }}
              InputLabelProps={{ sx: { lineHeight: "1em" } }}
              disabled={loading}
            />

            <TextField
              label="Project Number"
              value={projectNumber}
              onChange={(e) => setProjectNumber(e.target.value)}
              placeholder="0001"
              size="small"
              sx={{ width: 120 }}
              InputProps={{
                sx: { height: 32 },
              }}
              inputProps={{ maxLength: 10 }}
              InputLabelProps={{ sx: { lineHeight: "1em" } }}
              disabled={loading}
            />

          </Box>

          {/* --- ROW 2: Font settings + icon + mask toggle --- */}
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">

            {/* Font Size */}
            <FormControl sx={{ width: 120 }} size="small">
              <InputLabel sx={{ lineHeight: "1em" }}>Font Size</InputLabel>
              <Select
                value={fontSize}
                label="Font Size"
                onChange={(e) => setFontSize(e.target.value)}
                disabled={loading}
                sx={{ height: 32, display: "flex", alignItems: "center" }}
              >
                {fontSizes.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size} pt
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Font Style */}
            <FormControl sx={{ width: 150 }} size="small">
              <InputLabel sx={{ lineHeight: "1em" }}>Font Style</InputLabel>
              <Select
                value={fontFamily}
                label="Font Style"
                onChange={(e) => setFontFamily(e.target.value)}
                disabled={loading}
                sx={{ height: 32, display: "flex", alignItems: "center" }}
              >
                {fontFamilies.map((font) => (
                  <MenuItem key={font} value={font}>
                    {font}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Custom Icon */}
            <Button
              variant="outlined"
              component="label"
              size="small"
              startIcon={<PhotoCamera sx={{ fontSize: 18 }} />}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                height: 32,
                minHeight: 32,
                padding: "0 12px",
              }}
            >
              Custom Icon
              <input
                type="file"
                hidden
                accept="image/png, image/jpeg"
                onChange={handleLogoUpload}
              />
            </Button>

            {/* Masks Switch */}
            <FormControlLabel
              control={
                <Switch
                  checked={withMasks}
                  onChange={(e) => setWithMasks(e.target.checked)}
                  size="small"
                />
              }
              label="With Masks"
              sx={{
                m: 0,
                ".MuiFormControlLabel-label": { fontSize: 14 },
              }}
            />
          </Box>

          {/* --- Generate Report Button --- */}
          {!isLoading
            ? <Button
              variant="contained"
              fullWidth
              startIcon={<PublishIcon />}
              onClick={() => handleGenerateReport(false)}
              disabled={!props.selectedTemplate || loading}
              size="small"
              sx={{
                minWidth: 160,
                height: 36,
                borderRadius: 2,
                fontWeight: 500,
              }}
            >
              Generate Report
            </Button>
            :
            <Loading type="circular" text="Generating Report..." />
          }
        </Box>
      </Box >

      <Dialog
        open={htmlPreviewOpen}
        onClose={() => setHtmlPreviewOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Template Preview
          <IconButton onClick={() => setHtmlPreviewOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            bgcolor: "#f5f5f5",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            p: 4,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              width: "794px", // A4 width
              minHeight: "1123px", // A4 height
              backgroundColor: "white",
              overflow: "auto",
              p: 3,
              boxShadow: 3,
              borderRadius: 2,
            }}
          >
            <div
              dangerouslySetInnerHTML={{ __html: htmlContent }}
              style={{
                width: "100%",
                height: "100%",
                overflowWrap: "break-word",
              }}
            />
          </Paper>
        </DialogContent>
      </Dialog>

    </>
  );
}
